import React, { useEffect, useMemo, useRef, useState } from "react";
import "./eventsCalendar.scss";
import moment from "moment";
import classNames from "classnames";
import { DeleteLeague, GetLeague, PostLeague, PutLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { useAppDispatch } from "hooks/redux";
import { StatusCode } from "api/protocols";
import axios, { CancelToken } from "axios";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { useTranslation } from "react-i18next";
import { ILeague } from "redux/reducers/models/league";
import {
  DeleteTournament,
  GetTournament,
  PostTournament,
  PutTournament,
} from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { ITournament } from "redux/reducers/models/tournament";
import { DeleteEvent, GetEvent, PostEvent, PutEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { IEvent } from "redux/reducers/models/event";
import { Badge } from "components/badge/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import {
  faCalendar,
  faClock,
  faLongArrowRight,
  faPlus,
  faSpinnerThird,
  faUserGroup,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import NewEventDrawer, { IEventSettings, TEventDrawerMode } from "./eventsCalendarDrawers/NewEventDrawer";
import { formatDate } from "helpers/Helpers";
import { ButtonNew as Button } from "components/buttonNew";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import ScheduleEventModal, { IApplicationParams } from "./ScheduleEventModal";
import { PostEventApplicationTeeSheet, PostEventApplicationTemplate, PutEventApplicationTeeSheet, PutEventApplicationTemplate, TEventApplication } from "api/rpc/2024-04/facilityAdmin/event/eventApplication";

type TEventFilter = "all_events" | "golf" | "hospitality";

interface IProps {
  currentDate: Date;
  filter: TEventFilter;
  newEventDrawerOpen: boolean;
  closeNewEventDrawer: () => void;
  openNewEventDrawer: () => void;
}

type TCalendarEventType = "league" | "tournament" | "wedding" | "banquet" | "other";

interface IEvents {
  leagues: Array<ILeague>;
  tournaments: Array<ITournament>;
  hospitalityEvents: Array<IEvent>;
}

type TCalendarEvent = {
  id: number;
  name: string;
  start: string;
  end: string;
  type: TCalendarEventType;
  total_participant_count: number | null;
  start_time: string;
};

const sameEvent = (a: TCalendarEvent, b: TCalendarEvent) => a?.type === b?.type && a?.id === b?.id;

type EventsByDate = {
  [date: string]: TCalendarEvent[];
};

const TODAYS_DATE = new Date();

const MAX_EVENTS_PER_CELL = 4;

export default function EventsCalendar(props: IProps) {
  const { currentDate, filter, newEventDrawerOpen, closeNewEventDrawer, openNewEventDrawer } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const allEventsPopoverRef = useRef();
  const [displayNewEvent, setDisplayNewEvent] = useState("");
  const [presetSettings, setPresetSettings] = useState<IEventSettings>({
    name: "",
    type: "golf",
    category: "league",
    start_date: new Date(),
    end_date: new Date(),
    start_time: "12:00",
    week_day: "Monday",
    tournament_type: "regular",
    description: "",
  });

  const [applicationRefresh, setApplicationRefresh] = useState(0);

  const [eventDrawerMode, setEventDrawerMode] = useState<TEventDrawerMode>("create");

  const [isDeleteEventWarningOpen, setIsDeleteEventWarningOpen] = useState<boolean>(false);
  const [isDuplicateEventWarningOpen, setIsDuplicateEventWarningOpen] = useState<boolean>(false);

  const [events, setEvents] = useState<IEvents>({
    leagues: [],
    tournaments: [],
    hospitalityEvents: [],
  });

  const [maxRows, setMaxRows] = useState<number>(4);

  const [eventCounts, setEventCounts] = useState({
    leagues: 0,
    tournaments: 0,
    weddings: 0,
    banquets: 0,
    other: 0,
  });

  // Overlay loader
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);

  // All events popover
  const [allEventsPopover, setAllEventsPopover] = useState<{ date: string }>({
    date: "",
  });

  // Event details hover
  const [eventDetailsHover, setEventDetailsHover] = useState<{ event: TCalendarEvent; date: string }>({
    event: null,
    date: "",
  });

  const { state: scheduleModal, updateModal: updateScheduleModal, closeModal: closeScheduleModal } = useModal<{
    key: "template" | "tee-sheet",
    eventId: number;
    eventType: "league" | "tournament",
    application: TEventApplication,
    tournament_type: "regular" | "shotgun" | "crossover"
  }>({ key: "tee-sheet", eventId: null, eventType: "league", application: null, tournament_type: "regular" });

  useOutsideAlerter(allEventsPopoverRef, () => {
    setAllEventsPopover({
      date: "",
    });
  });

  // Filter the events based on filter selection
  const filteredEvents = useMemo<IEvents>(() => {
    switch (filter) {
      case "golf":
        return {
          leagues: events.leagues,
          tournaments: events.tournaments,
          hospitalityEvents: [],
        };

      case "hospitality":
        return {
          leagues: [],
          tournaments: [],
          hospitalityEvents: events.hospitalityEvents,
        };

      case "all_events":
      default:
        return events;
    }
  }, [events, filter]);

  const allEvents: TCalendarEvent[] = useMemo(() => {
    // Leagues
    const leagues = filteredEvents.leagues.map(l => {
      const start = moment(l.start_date);
      const end = l.end_date ? moment(l.end_date) : start;

      return {
        id: l.id,
        name: l.name,
        start: start.format("DD-MM-YYYY"),
        end: end.format("DD-MM-YYYY"),
        type: "league" as TCalendarEventType,
        total_participant_count: l?.total_participant_count,
        start_time: l?.start_time,
      };
    });

    // Tournaments
    const tournaments = filteredEvents.tournaments.map(t => {
      const start = moment(t.date);
      const end = t.end_date ? moment(t.end_date) : start;

      return {
        id: t.id,
        name: t.name,
        start: start.format("DD-MM-YYYY"),
        end: end.format("DD-MM-YYYY"),
        type: "tournament" as TCalendarEventType,
        total_participant_count: t?.total_participant_count,
        start_time: t?.start_time,
      };
    });

    // Hospitality Event
    const hospitality = filteredEvents.hospitalityEvents.map(e => {
      const start = moment(e.date);
      const end = e.end_date ? moment(e.end_date) : start;

      return {
        id: e.id,
        name: e.name,
        start: start.format("DD-MM-YYYY"),
        end: end.format("DD-MM-YYYY"),
        type: e.type as TCalendarEventType,
        total_participant_count: null as number | null,
        start_time: e?.start_time,
      };
    });

    return [...leagues, ...tournaments, ...hospitality];
  }, [filteredEvents]);

  const days = useMemo(() => getMonthGrid(currentDate), [currentDate]);

  // Events arrays set on each day
  const eventsByDate = useMemo(() => {
    const map: EventsByDate = {};

    const addEvent = (dateKey: string, event: TCalendarEvent) => {
      if (!map[dateKey]) {
        map[dateKey] = [];
      }
      map[dateKey].push(event);
    };

    // Leagues
    filteredEvents.leagues.forEach(league => {
      if (!league.start_date || !league.week_day) {
        return;
      }

      const start = moment(league.start_date);
      const end = league.end_date ? moment(league.end_date) : start.clone();

      const targetWeekday = moment().day(league.week_day).day();
      const current = start.clone();

      while (current.isSameOrBefore(end, "day")) {
        if (current.day() === targetWeekday) {
          const key = current.format("DD-MM-YYYY");

          addEvent(key, {
            id: league.id,
            name: league.name,
            start: key,
            end: key, //Leagues are always single day, but occur every week_day until the end date,
            type: "league",
            total_participant_count: league?.total_participant_count,
            start_time: league?.start_time,
          });
        }

        current.add(1, "day");
      }
    });

    // Tournaments
    filteredEvents.tournaments.forEach(tournament => {
      if (!tournament.date) {
        return;
      }

      const start = moment(tournament.date);
      const end = tournament.end_date ? moment(tournament.end_date) : start.clone();

      const current = start.clone();

      while (current.isSameOrBefore(end, "day")) {
        const key = current.format("DD-MM-YYYY");

        addEvent(key, {
          id: tournament.id,
          name: tournament.name,
          start: start.format("DD-MM-YYYY"),
          end: end.format("DD-MM-YYYY"),
          type: "tournament",
          total_participant_count: tournament?.total_participant_count,
          start_time: tournament?.start_time,
        });

        current.add(1, "day");
      }
    });

    // Hospitality events
    filteredEvents.hospitalityEvents.forEach(event => {
      if (!event.date) {
        return;
      }

      const start = moment(event.date);
      const end = event.end_date ? moment(event.end_date) : start.clone();

      const current = start.clone();

      while (current.isSameOrBefore(end, "day")) {
        const key = current.format("DD-MM-YYYY");

        addEvent(key, {
          id: event.id,
          name: event.name,
          start: start.format("DD-MM-YYYY"),
          end: end.format("DD-MM-YYYY"),
          type: event.type as TCalendarEventType,
          total_participant_count: null,
          start_time: event?.start_time,
        });

        current.add(1, "day");
      }
    });

    // Sort events alphabetically
    Object.keys(map).forEach(key => {
      map[key].sort((a, b) => a.name.localeCompare(b.name));
    });

    return map;
  }, [filteredEvents]);

  // Build out the rows indicies for the events. For determining stretched/single day events
  const dayRows = useMemo(() => {
    const totalRows: Array<Array<TCalendarEvent> | null> = [];
    days.forEach((day, index) => {
      const dayString = day ? moment(day)?.format("DD-MM-YYYY") : "";
      if (day) {
        const dayEvents = eventsByDate[dayString] || [];

        // Events that span a length of time
        const spanningEvents = dayEvents.filter(e => e.end && e.start !== e.end);

        // Single day events
        const singleDayEvents = dayEvents.filter(e => e.start === e.end || !e.end);

        // Prev day rows
        const prevDayRows = totalRows[index - 1];

        // Current day rows
        const rows: (TCalendarEvent | null)[] = Array(maxRows).fill(null);

        let rowIndex = 0;
        //Spanning events from prev day
        const filteredSpanningEvents = spanningEvents?.filter(event => {
          const foundPrevDayEventIndex = prevDayRows ? prevDayRows?.findIndex(item => sameEvent(item, event)) : -1;
          return foundPrevDayEventIndex !== -1;
        });

        // Handle spanning events from prev day first
        filteredSpanningEvents.forEach(event => {
          //Determine if the event is already displayed on the prev day (to stretch out to current day)
          const foundPrevDayEventIndex = prevDayRows ? prevDayRows?.findIndex(item => sameEvent(item, event)) : -1;

          // Set prev day's index
          if (foundPrevDayEventIndex !== -1) {
            rowIndex = foundPrevDayEventIndex;
          }

          if (rowIndex < maxRows) {
            // Don't display stretch out blocks if prev day doesn't contain the event
            if (
              event?.type !== "league" &&
              foundPrevDayEventIndex === -1 &&
              dayString > event?.start &&
              dayString <= event?.end
            ) {
              return;
            } else {
              rows[rowIndex] = event;
            }
          }
          // Get next available index
          const nextRowIndex = rows?.findIndex(row => row === null);
          rowIndex = nextRowIndex !== -1 ? nextRowIndex : rowIndex++;
        });

        // Filter out the prev day rows that were just added. Check if there are remaining spanning day events
        const filteredPrevDayRows = prevDayRows
          ? prevDayRows?.filter(e => !filteredSpanningEvents?.some(filterEvent => sameEvent(filterEvent, e)))
          : [];

        // Handle the rest of the events
        spanningEvents.forEach(event => {
          //Determine if the event is already displayed on the prev day (to stretch out to current day)
          const foundPrevDayEventIndex = filteredPrevDayRows
            ? filteredPrevDayRows?.findIndex(item => sameEvent(item, event))
            : -1;

          // Set prev day's index
          if (foundPrevDayEventIndex !== -1) {
            rowIndex = foundPrevDayEventIndex;
          }

          if (rowIndex < maxRows) {
            // Don't display stretch out blocks if prev day doesn't contain the event
            if (
              event?.type !== "league" &&
              foundPrevDayEventIndex === -1 &&
              dayString > event?.start &&
              dayString <= event?.end
            ) {
              return;
            } else {
              rows[rowIndex] = event;
            }
          }
          // Get next available index
          const nextRowIndex = rows?.findIndex(row => row === null);
          rowIndex = nextRowIndex !== -1 ? nextRowIndex : rowIndex++;
        });

        let singleIndex = 0;

        // Fill single dat events
        for (let i = 0; i < rows.length; i++) {
          if (!rows[i] && singleIndex < singleDayEvents.length) {
            rows[i] = singleDayEvents[singleIndex];
            singleIndex++;
          }
        }

        // Check if more than 4 events exist on a day
        const overFlowEvents = eventsByDate[dayString]?.length > MAX_EVENTS_PER_CELL;
        let filteredRows = rows;
        // If more than 4 events exist for the day, remove the last event and show 'More' badge (when rendering the rows)
        if (overFlowEvents && rows?.every(r => r != null) && rows[MAX_EVENTS_PER_CELL - 1] != null) {
          filteredRows = filteredRows?.filter((_, i) => i + 1 < MAX_EVENTS_PER_CELL);
        }

        totalRows.push(filteredRows);
      } else {
        totalRows.push(Array(maxRows).fill(null) as Array<null>);
      }
    });

    return totalRows;
  }, [days, eventsByDate, maxRows]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadEvents(source.token);
    return () => source.cancel();
  }, [currentDate?.getFullYear()]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadEvents(source.token);
    return () => source.cancel();
  }, [currentDate?.getMonth()]);

  useEffect(() => {
    setMaxRows(Math.min(allEvents?.length ?? 0, MAX_EVENTS_PER_CELL));
  }, [allEvents]);

  // Build counts in legend
  useEffect(() => {
    const leagueCount =
      filteredEvents?.leagues?.filter(
        league =>
          new Date(league?.start_date)?.getMonth() === currentDate?.getMonth() ||
          (league?.end_date && new Date(league?.end_date)?.getMonth() === currentDate?.getMonth()) ||
          (league?.end_date &&
            moment(currentDate)?.isSameOrAfter(moment(league?.start_date)) &&
            moment(currentDate)?.isSameOrBefore(moment(league?.end_date))),
      )?.length ?? 0;
    const tournamentCount =
      filteredEvents?.tournaments?.filter(
        tournament =>
          new Date(tournament?.date)?.getMonth() === currentDate?.getMonth() ||
          (tournament?.end_date && new Date(tournament?.end_date)?.getMonth() === currentDate?.getMonth()),
      )?.length ?? 0;
    const weddingCount =
      filteredEvents?.hospitalityEvents?.filter(
        wedding =>
          wedding?.type === "wedding" &&
          (new Date(wedding?.date)?.getMonth() === currentDate?.getMonth() ||
            (wedding?.end_date && new Date(wedding?.end_date)?.getMonth() === currentDate?.getMonth())),
      )?.length ?? 0;

    const banquetCount =
      filteredEvents?.hospitalityEvents?.filter(
        banquet =>
          banquet?.type === "banquet" &&
          (new Date(banquet?.date)?.getMonth() === currentDate?.getMonth() ||
            (banquet?.end_date && new Date(banquet?.end_date)?.getMonth() === currentDate?.getMonth())),
      )?.length ?? 0;

    const otherCount =
      filteredEvents?.hospitalityEvents?.filter(
        other =>
          other?.type === "other" &&
          (new Date(other?.date)?.getMonth() === currentDate?.getMonth() ||
            (other?.end_date && new Date(other?.end_date)?.getMonth() === currentDate?.getMonth())),
      )?.length ?? 0;

    setEventCounts(prev => ({
      ...prev,
      leagues: leagueCount,
      tournaments: tournamentCount,
      weddings: weddingCount,
      banquets: banquetCount,
      other: otherCount,
    }));
  }, [currentDate?.getMonth(), filteredEvents]);

  async function loadEvents(cancelToken?: CancelToken) {
    setLoadingEvents(true);
    const [leagueRes, tournamentRes, hospitalityEventRes] = await Promise.all([
      GetLeague({ year: currentDate?.getFullYear(), month: currentDate?.getMonth() + 1 }, false, cancelToken),
      GetTournament({ year: currentDate?.getFullYear(), month: currentDate?.getMonth() + 1 }, false, cancelToken),
      GetEvent({ year: currentDate?.getFullYear(), month: currentDate?.getMonth() + 1 }, false, cancelToken),
    ]);
    setLoadingEvents(false);
    if (leagueRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setEvents(prev => ({ ...prev, leagues: [] }));
      dispatch(showError(typeof leagueRes?.data === "string" ? leagueRes?.data : t("Error getting leagues")));
      return;
    }
    if (tournamentRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setEvents(prev => ({ ...prev, tournaments: [] }));
      dispatch(
        showError(typeof tournamentRes?.data === "string" ? tournamentRes?.data : t("Error getting tournaments")),
      );
      return;
    }

    if (hospitalityEventRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setEvents(prev => ({ ...prev, hospitalityEvents: [] }));
      dispatch(
        showError(
          typeof hospitalityEventRes?.data === "string"
            ? hospitalityEventRes?.data
            : t("Error getting hospitality events"),
        ),
      );
      return;
    }
    setEvents({
      leagues: leagueRes?.data,
      tournaments: tournamentRes?.data,
      hospitalityEvents: hospitalityEventRes?.data,
    });
  }

  function getMonthGrid(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const startDay = firstDayOfMonth.getDay(); // 0 is Sunday
    const totalDays = lastDayOfMonth.getDate();

    const days: Date[] = [];

    // Previous month padding
    for (let i = startDay - 1; i >= 0; i--) {
      //days.push(new Date(year, month, -i));
      days.push(null);
    }

    // Current month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i));
    }

    // Complete the last week only so months that fit in 5 weeks
    // do not render an extra empty row.
    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return days;
  }

  function getTypeString(type: string) {
    switch (type) {
      case "league":
        return t("League");
      case "tournament":
        return t("Tournament");
      case "wedding":
        return t("Wedding");
      case "banquet":
        return t("Banquet");
      case "other":
        return t("Other");
      default:
        return t("Other");
    }
  }

  function resetPresetSettings() {
    setEventDrawerMode("create");
    setPresetSettings({
      name: "",
      type: "golf",
      category: "league",
      start_date: new Date(),
      end_date: new Date(),
      start_time: "12:00",
      week_day: "Monday",
      tournament_type: "regular",
      description: "",
    });
  }

  function mapCalendarEventToSettings(calendarEvent: TCalendarEvent): IEventSettings | null {
    if (calendarEvent.type === "league") {
      const league = events.leagues.find(l => l.id === calendarEvent.id);
      if (!league) {
        return null;
      }

      return {
        id: league.id,
        name: league.name,
        type: "golf",
        category: "league",
        start_date: moment(league.start_date, "YYYY-MM-DD").toDate(),
        end_date: moment(league.end_date ?? league.start_date, "YYYY-MM-DD").toDate(),
        start_time: league.start_time?.substring(0, 5) ?? "12:00",
        week_day: league.week_day ?? "Monday",
        tournament_type: "regular",
        description: "",
      };
    }

    if (calendarEvent.type === "tournament") {
      const tournament = events.tournaments.find(t => t.id === calendarEvent.id);
      if (!tournament) {
        return null;
      }

      return {
        id: tournament.id,
        name: tournament.name,
        type: "golf",
        category: "tournament",
        start_date: moment(tournament.date, "YYYY-MM-DD").toDate(),
        end_date: moment(tournament.end_date ?? tournament.date, "YYYY-MM-DD").toDate(),
        start_time: tournament.start_time?.substring(0, 5) ?? "12:00",
        week_day: "Monday",
        tournament_type: tournament?.type,
        description: tournament.description ?? "",
      };
    }

    const hospitality = events.hospitalityEvents.find(e => e.id === calendarEvent.id);
    if (!hospitality) {
      return null;
    }

    return {
      id: hospitality.id,
      name: hospitality.name,
      type: "hospitality",
      category: hospitality.type as IEventSettings["category"],
      start_date: moment(hospitality.date, "YYYY-MM-DD").toDate(),
      end_date: moment(hospitality.end_date ?? hospitality.date, "YYYY-MM-DD").toDate(),
      start_time: hospitality.start_time?.substring(0, 5) ?? "12:00",
      week_day: "Monday",
      tournament_type: "regular",
      description: hospitality.description ?? "",
    };
  }

  function openEditEvent(calendarEvent: TCalendarEvent) {
    const settings = mapCalendarEventToSettings(calendarEvent);
    if (!settings) {
      dispatch(showError(t("Could not load event for editing")));
      return;
    }
    setEventDrawerMode("edit");
    setPresetSettings(settings);
    void openNewEventDrawer();
  }

  async function createEvent(settings: IEventSettings) {
    let eventCreated = false;
    if (!settings?.name) {
      dispatch(showError(t("Name field required")));
      return eventCreated;
    }

    dispatch(enqueue());

    if (settings.type === "golf") {
      // LEAGUE
      if (settings.category === "league") {
        const res = await PostLeague(
          {
            name: settings.name,
            week_day: settings.week_day as any,
            start_time: settings.start_time,
            start_date: formatDate(settings.start_date),
            end_date: formatDate(settings.end_date),
          },
          false,
        );
        if (res?.status !== StatusCode.OK) {
          dispatch(dequeue());
          dispatch(showError(typeof res.data === "string" ? res.data : t("Error creating league")));
          return eventCreated;
        }
        dispatch(showSuccess(t("League created successfully")));
        eventCreated = true;
      } else {
        // TOURNAMENT
        const res = await PostTournament(
          {
            name: settings.name,
            start_time: settings.start_time,
            date: formatDate(settings.start_date),
            end_date: formatDate(settings.end_date),
            description: settings.description,
            tournament_type: settings.tournament_type,
          },
          false,
        );
        if (res?.status !== StatusCode.OK) {
          dispatch(dequeue());
          dispatch(showError(typeof res.data === "string" ? res.data : t("Error creating tournament")));
          return eventCreated;
        }
        dispatch(showSuccess(t("Tournament created successfully")));
        eventCreated = true;
      }
    } else {
      // HOSPITALITY EVENT
      const res = await PostEvent(
        {
          name: settings.name,
          start_time: settings.start_time,
          date: formatDate(settings.start_date),
          end_date: formatDate(settings.end_date),
          type: settings?.category,
        },
        false,
      );
      if (res?.status !== StatusCode.OK) {
        dispatch(dequeue());
        dispatch(showError(typeof res.data === "string" ? res.data : t("Error creating hospitality event")));
        return eventCreated;
      }
      dispatch(showSuccess(t("Hospitality event created successfully")));
      eventCreated = true;
    }

    await loadEvents();
    dispatch(dequeue());
    return eventCreated;
  }

  function getEventDetailsRoute(settings: IEventSettings): string | null {
    if (!settings.id) {
      return null;
    }

    if (settings.type === "golf") {
      if (settings.category === "league") {
        return `/admin/league/${settings.id}/home`;
      }
      if (settings.category === "tournament") {
        return `/admin/tournament/${settings.id}/home`;
      }
      return null;
    }

    return `/admin/event/${settings.id}/home`;
  }

  function handleViewEventDetails() {
    const route = getEventDetailsRoute(presetSettings);
    if (!route) {
      return;
    }
    window.open(route, "_blank");
  }

  async function duplicateEvent() {
    const copy = {
      ...presetSettings,
      name: `${presetSettings.name} (${t("Copy")})`,
    };
    const success = await createEvent(copy);

    if (success) {
      closeNewEventDrawer();
      resetPresetSettings();
      setIsDuplicateEventWarningOpen(false);
    }
  }

  async function deleteEvent() {
    const { id, type, category } = presetSettings;
    if (!id) {
      return;
    }

    dispatch(enqueue());
    let res;
    if (type === "golf" && category === "league") {
      res = await DeleteLeague({ id }, true);
    } else if (type === "golf" && category === "tournament") {
      res = await DeleteTournament({ id }, true);
    } else {
      res = await DeleteEvent({ id }, true);
    }

    if (res?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error deleting event")));
      return;
    }

    dispatch(showSuccess(t("Event deleted successfully")));
    await loadEvents();
    dispatch(dequeue());
    resetPresetSettings();
    setIsDeleteEventWarningOpen(false);
    closeNewEventDrawer();
  }

  async function updateEvent(settings: IEventSettings) {
    let eventUpdated = false;

    if (!settings?.name) {
      dispatch(showError(t("Name field required")));
      return eventUpdated;
    }

    if (!settings.id) {
      dispatch(showError(t("Could not update event")));
      return eventUpdated;
    }

    dispatch(enqueue());

    if (settings.type === "golf") {
      if (settings.category === "league") {
        const res = await PutLeague(
          {
            id: settings.id,
            name: settings.name,
            week_day: settings.week_day as any,
            start_time: settings.start_time,
            start_date: formatDate(settings.start_date),
            end_date: formatDate(settings.end_date),
          },
          false,
        );

        if (res?.status !== StatusCode.OK) {
          dispatch(dequeue());
          dispatch(showError(typeof res.data === "string" ? res.data : t("Error updating league")));
          return eventUpdated;
        }

        dispatch(showSuccess(t("League updated successfully")));
        eventUpdated = true;
      } else {
        const res = await PutTournament(
          {
            id: settings.id,
            name: settings.name,
            start_time: settings.start_time,
            date: formatDate(settings.start_date),
            end_date: formatDate(settings.end_date),
            description: settings.description,
            tournament_type: settings?.tournament_type,
          },
          false,
        );

        if (res?.status !== StatusCode.OK) {
          dispatch(dequeue());
          dispatch(showError(typeof res.data === "string" ? res.data : t("Error updating tournament")));
          return eventUpdated;
        }

        dispatch(showSuccess(t("Tournament updated successfully")));
        eventUpdated = true;
      }
    } else {
      const res = await PutEvent(
        {
          id: settings.id,
          name: settings.name,
          start_time: settings.start_time,
          date: formatDate(settings.start_date),
          end_date: formatDate(settings.end_date),
        },
        false,
      );

      if (res?.status !== StatusCode.OK) {
        dispatch(dequeue());
        dispatch(showError(typeof res.data === "string" ? res.data : t("Error updating hospitality event")));
        return eventUpdated;
      }

      dispatch(showSuccess(t("Hospitality event updated successfully")));
      eventUpdated = true;
    }

    await loadEvents();
    dispatch(dequeue());
    return eventUpdated;
  }

  async function saveEvent(settings: IEventSettings) {
    if (eventDrawerMode === "edit") {
      return updateEvent(settings);
    }

    return createEvent(settings);
  }

  async function handleApplyEvent(params: IApplicationParams, type: "new" | "edit", isShotGun: boolean) {
    // New application
    if (type === "new") {
      if (params?.page === "template" && params?.template_id) {
        if (isShotGun) {
          const durationTime = params?.duration;
          const durationSplit = durationTime.split(":");
          const durationMins = +durationSplit[0] * 60 + +durationSplit[1];
          const leadTime = params?.leadTime;
          const leadTimeSplit = leadTime.split(":");
          const leadTimeMins = +leadTimeSplit[0] * 60 + +leadTimeSplit[1];

          const res = await PostEventApplicationTemplate({
            event_type: scheduleModal?.eventType,
            event_id: scheduleModal?.eventId,
            template_id: params?.template_id,
            start_time: params?.start_time,
            end_time: params?.end_time,
            division_id: params?.division_id,
            course_id: params?.course_id,
            application_type: "shotgun",
            shotgun_template_id: params?.shotgunTemplateId,
            duration: durationMins,
            lead_time: leadTimeMins,
            turn_division_id: params?.turn_division_id,
            booking_category_ids: params?.bookingCategoryIds,
          }, true);
          if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error applying shotgun event to tee sheet template")));
            return false;
          }
          setApplicationRefresh(prev => prev + 1);
          dispatch(showSuccess(t("Successfully applied shotgun event to tee sheet template")));
          return true;
        }
        // Apply to tee sheet template
        const res = await PostEventApplicationTemplate({
          event_type: scheduleModal?.eventType,
          event_id: scheduleModal?.eventId,
          template_id: params?.template_id,
          start_time: params?.start_time,
          end_time: params?.end_time,
          division_id: params?.division_id,
          course_id: params?.course_id,
        }, true);

        if (res?.status !== StatusCode.OK) {
          dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error applying event to tee sheet template")));
          return false;
        }
        setApplicationRefresh(prev => prev + 1);
        dispatch(showSuccess(t("Successfully applied event to tee sheet template")));
        return true;
      } else {
        if (isShotGun) {
          const durationTime = params?.duration;
          const durationSplit = durationTime.split(":");
          const durationMins = +durationSplit[0] * 60 + +durationSplit[1];
          const leadTime = params?.leadTime;
          const leadTimeSplit = leadTime.split(":");
          const leadTimeMins = +leadTimeSplit[0] * 60 + +leadTimeSplit[1];

          const res = await PostEventApplicationTeeSheet({
            event_type: scheduleModal?.eventType,
            event_id: scheduleModal?.eventId,
            start_time: params?.start_time,
            end_time: params?.end_time,
            division_id: params?.division_id,
            course_id: params?.course_id,
            start_date: params?.start_date,
            end_date: params?.end_date,
            recurrence_type: params?.recurrence_type,
            repeat_amount: Number(params?.repeat_amount),
            interval: params?.interval,
            weekdays: params?.weekdays,
            monthly_rule_type: params?.monthly_rule_type,
            ends_type: params?.ends_type,
            ends_on_date: params?.ends_on_date,
            ends_after_occurrences: Number(params?.ends_after_occurrences),
            application_type: "shotgun",
            shotgun_template_id: params?.shotgunTemplateId,
            duration: durationMins,
            lead_time: leadTimeMins,
            turn_division_id: params?.turn_division_id,
            booking_category_ids: params?.bookingCategoryIds,
          }, true);
          if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error applying shotgun event to tee sheet template")));
            return false;
          }
          setApplicationRefresh(prev => prev + 1);
          dispatch(showSuccess(t("Successfully applied shotgun event to tee sheet template")));
          return true;
        }
        // Apply to live tee sheet
        const res = await PostEventApplicationTeeSheet({
          event_type: scheduleModal?.eventType,
          event_id: scheduleModal?.eventId,
          start_time: params?.start_time,
          end_time: params?.end_time,
          division_id: params?.division_id,
          course_id: params?.course_id,
          start_date: params?.start_date,
          end_date: params?.end_date,
          recurrence_type: params?.recurrence_type,
          repeat_amount: Number(params?.repeat_amount),
          interval: params?.interval,
          weekdays: params?.weekdays,
          monthly_rule_type: params?.monthly_rule_type,
          ends_type: params?.ends_type,
          ends_on_date: params?.ends_on_date,
          ends_after_occurrences: Number(params?.ends_after_occurrences),
        }, true);

        if (res?.status !== StatusCode.OK) {
          dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error applying event to tee sheet(s)")));
          return false;
        }
        setApplicationRefresh(prev => prev + 1);
        dispatch(showSuccess(t("Successfully applied event to tee sheet(s)")));
        return true;
      }
    } else {
      // Edit application
      if (params?.page === "template" && params?.template_id) {
        // Apply to tee sheet template
        const res = await PutEventApplicationTemplate({
          id: scheduleModal?.application?.id,
          start_time: params?.start_time,
          end_time: params?.end_time,
        }, true);

        if (res?.status !== StatusCode.OK) {
          dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error updating event application")));
          return false;
        }
        setApplicationRefresh(prev => prev + 1);
        dispatch(showSuccess(t("Successfully updated event application")));
        return true;
      } else {
        // Apply to live tee sheet
        const res = await PutEventApplicationTeeSheet({
          id: scheduleModal?.application?.id,
          event_type: scheduleModal?.eventType,
          event_id: scheduleModal?.eventId,
          start_time: params?.start_time,
          end_time: params?.end_time,
          start_date: params?.start_date,
          end_date: params?.end_date,
          recurrence_type: params?.recurrence_type,
          repeat_amount: Number(params?.repeat_amount),
          interval: params?.interval,
          weekdays: params?.weekdays,
          monthly_rule_type: params?.monthly_rule_type,
          ends_type: params?.ends_type,
          ends_on_date: params?.ends_on_date,
          ends_after_occurrences: Number(params?.ends_after_occurrences),
        }, true);

        if (res?.status !== StatusCode.OK) {
          dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error updating event application")));
          return false;
        }

        setApplicationRefresh(prev => prev + 1);
        dispatch(showSuccess(t("Successfully updated event application")));
        return true;
      }
    }
  }

  console.log(
    "%csrc/pages/secure/facility/events/calendar/eventsCalendar/EventsCalendar.tsx:163 eventsByDate",
    "color: #007acc;",
    eventsByDate,
  );

  console.log(
    "%csrc/pages/secure/facility/events/calendar/eventsCalendar/EventsCalendar.tsx:396 dayRows",
    "color: #007acc;",
    dayRows,
  );

  return (
    <>
      <div className="ui-events-calendar">
        {moment?.weekdays()?.map((day, i) => {
          return (
            <div key={i} className="ui-events-calendar-day-title">
              <p>{day?.substring(0, 3)?.toUpperCase()}</p>
            </div>
          );
        })}
        {days.map((day, index, days) => {
          // Highlight the current day
          const isCurrentDay = day
            ? day.getDate() === TODAYS_DATE.getDate() &&
            day?.getMonth() === TODAYS_DATE?.getMonth() &&
            day?.getFullYear() === TODAYS_DATE?.getFullYear()
            : false;
          const dayString = day ? moment(day)?.format("DD-MM-YYYY") : "";
          const nextDay = days[index + 1];
          const nextDayString = nextDay ? moment(nextDay)?.format("DD-MM-YYYY") : "";
          if (day) {
            const isLastRow = index >= days.length - 7;

            // Check if more than 4 events exist on a day
            const overFlowEvents = eventsByDate[dayString]?.length > MAX_EVENTS_PER_CELL;

            return (
              <div
                key={index}
                onMouseEnter={!eventsByDate[dayString] ? () => setDisplayNewEvent(dayString) : undefined}
                onMouseLeave={!eventsByDate[dayString] ? () => setDisplayNewEvent("") : undefined}
                className={classNames("ui-events-calendar-day", {
                  "ui-events-calendar-day-empty": !eventsByDate[dayString],
                })}
              >
                <div
                  className={classNames("ui-events-calendar-day-container", {
                    "ui-events-calendar-day-current": isCurrentDay,
                  })}
                >
                  <p>{day.getDate()}</p>
                  {eventsByDate[dayString] ? (
                    <div className="ui-events-calendar-events-container">
                      {dayRows[index]?.map((eventForRow, rowIndex) => {
                        if (!eventForRow) {
                          return (
                            <div
                              key={rowIndex}
                              className="ui-events-calendar-events-row ui-events-calendar-events-row-empty"
                            />
                          );
                        }

                        const isStart = dayString === eventForRow.start;
                        const isEnd = dayString === eventForRow.end;
                        const isStretch = !isStart;

                        const singleDate =
                          isStart &&
                          eventsByDate[nextDayString] &&
                          eventsByDate[nextDayString]?.some(e => e?.id == eventForRow?.id);

                        return (
                          <div
                            key={rowIndex}
                            className={classNames("ui-events-calendar-events-row-wrapper", {
                              [`ui-events-calendar-events-row-wrapper-${eventForRow?.type}`]: eventForRow?.type,
                            })}
                          >
                            <div
                              onMouseEnter={
                                !allEventsPopover?.date
                                  ? () => setEventDetailsHover({ event: eventForRow, date: dayString })
                                  : undefined
                              }
                              onMouseLeave={
                                !allEventsPopover?.date
                                  ? () => setEventDetailsHover({ event: null, date: "" })
                                  : undefined
                              }
                              className={classNames("ui-events-calendar-events-row cursor-pointer", {
                                "ui-events-calendar-events-row-start-date": singleDate,
                                "ui-events-calendar-events-row-end-date": isEnd && !isStart,
                                "ui-events-calendar-events-row-stretch": isStretch,
                                [`ui-events-calendar-events-row-${eventForRow?.type}`]: eventForRow?.type,
                              })}
                              onClick={() => openEditEvent(eventForRow)}
                            >
                              <span className="ui-events-calendar-events-row-text-overflow">
                                {isStart ? eventForRow.name : null}
                              </span>
                            </div>
                            {!allEventsPopover?.date &&
                              eventDetailsHover?.event?.id === eventForRow?.id &&
                              dayString === eventDetailsHover?.date ? (
                              <div
                                className={classNames("ui-events-calendar-popover ui-events-calendar-hover", {
                                  "ui-events-calendar-popover-above": isLastRow,
                                })}
                              >
                                <div className="ui-events-calendar-hover-header">
                                  <span className="ui-events-calendar-hover-primary">
                                    {eventDetailsHover?.event?.name}
                                  </span>
                                  <span className="ui-events-calendar-hover-secondary">
                                    {eventDetailsHover?.event?.type === "league" ||
                                      eventDetailsHover?.event?.type === "tournament"
                                      ? t("Golf")
                                      : t("Hospitality")}{" "}
                                    • {getTypeString(eventDetailsHover?.event?.type)}
                                  </span>
                                </div>
                                <div className="ui-events-calendar-hover-body">
                                  <div className="ui-events-calendar-hover-row">
                                    <FontAwesomeIcon icon={faClock} />{" "}
                                    <span>{moment(eventDetailsHover?.event?.start_time, "HH:mm")?.format("LT")}</span>
                                  </div>
                                  <div className="ui-events-calendar-hover-row">
                                    <FontAwesomeIcon icon={faCalendar} />{" "}
                                    <span>
                                      {moment(eventDetailsHover?.event?.start, "DD-MM-YYYY")?.format("ll")}{" "}
                                      {eventDetailsHover?.event?.end &&
                                        eventDetailsHover?.event?.end !== eventDetailsHover?.event?.start ? (
                                        <>
                                          {" "}
                                          <FontAwesomeIcon icon={faLongArrowRight} />{" "}
                                          {moment(eventDetailsHover?.event?.end, "DD-MM-YYYY")?.format("ll")}
                                        </>
                                      ) : null}
                                    </span>
                                  </div>
                                  {eventDetailsHover?.event?.total_participant_count != null ? (
                                    <div className="ui-events-calendar-hover-row">
                                      <FontAwesomeIcon icon={faUserGroup} />{" "}
                                      <span>
                                        {eventDetailsHover?.event?.total_participant_count} {t("players")}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}

                      {/* More than 4 events exist of current cell, show "more" badge */}
                      {overFlowEvents ? (
                        <div className="relative">
                          <div
                            className="ui-events-calendar-events-row ui-events-calendar-events-row-more"
                            onClick={() => setAllEventsPopover({ date: dayString })}
                          >
                            <span className="ui-events-calendar-events-row-text-overflow">
                              {eventsByDate[dayString]?.length - dayRows[index]?.length} {t("more...")}
                            </span>
                          </div>
                          {allEventsPopover?.date === dayString ? (
                            <div
                              className={classNames("ui-events-calendar-popover", {
                                "ui-events-calendar-popover-above": isLastRow,
                              })}
                              ref={allEventsPopoverRef}
                            >
                              <div className="ui-events-calendar-popover-header">
                                <div>
                                  <span className="ui-events-calendar-popover-primary">{t("All events")}</span>
                                  <br />
                                  <span className="ui-events-calendar-popover-secondary">
                                    {moment(dayString, "DD-MM-YYYY")?.format("dddd")} •{" "}
                                    {moment(dayString, "DD-MM-YYYY")?.format("ll")}
                                  </span>
                                </div>
                                <FontAwesomeIcon
                                  onClick={e => {
                                    e.stopPropagation();
                                    setAllEventsPopover({ date: "" });
                                  }}
                                  className="ui-events-calendar-popover-secondary cursor-pointer"
                                  icon={faXmark}
                                />
                              </div>
                              <div className="ui-events-calendar-popover-body">
                                {eventsByDate[dayString]?.map((popoverEvent, popoverIndex) => {
                                  return (
                                    <div
                                      key={popoverIndex}
                                      className={classNames("ui-events-calendar-events-row-wrapper", {
                                        [`ui-events-calendar-events-row-wrapper-${popoverEvent?.type}`]:
                                          popoverEvent?.type,
                                      })}
                                    >
                                      <div
                                        onMouseEnter={() =>
                                          setEventDetailsHover({ event: popoverEvent, date: dayString })
                                        }
                                        onMouseLeave={() => setEventDetailsHover({ event: null, date: "" })}
                                        className={classNames(
                                          "ui-events-calendar-events-row ui-events-calendar-events-row-start-date ui-events-calendar-events-row-end-date cursor-pointer",
                                          {
                                            [`ui-events-calendar-events-row-${popoverEvent?.type}`]: popoverEvent?.type,
                                          },
                                        )}
                                        onClick={() => openEditEvent(popoverEvent)}
                                      >
                                        <span className="ui-events-calendar-events-row-text-overflow">
                                          {popoverEvent.name}
                                        </span>
                                      </div>
                                      {eventDetailsHover?.event?.id === popoverEvent?.id &&
                                        dayString === eventDetailsHover?.date ? (
                                        <div
                                          className={classNames("ui-events-calendar-popover ui-events-calendar-hover", {
                                            "ui-events-calendar-popover-above": isLastRow,
                                          })}
                                        >
                                          <div className="ui-events-calendar-hover-header">
                                            <span className="ui-events-calendar-hover-primary">
                                              {eventDetailsHover?.event?.name}
                                            </span>
                                            <span className="ui-events-calendar-hover-secondary">
                                              {eventDetailsHover?.event?.type === "league" ||
                                                eventDetailsHover?.event?.type === "tournament"
                                                ? t("Golf")
                                                : t("Hospitality")}{" "}
                                              • {getTypeString(eventDetailsHover?.event?.type)}
                                            </span>
                                          </div>
                                          <div className="ui-events-calendar-hover-body">
                                            <div className="ui-events-calendar-hover-row">
                                              <FontAwesomeIcon icon={faClock} />{" "}
                                              <span>
                                                {moment(eventDetailsHover?.event?.start_time, "HH:mm")?.format("LT")}
                                              </span>
                                            </div>
                                            <div className="ui-events-calendar-hover-row">
                                              <FontAwesomeIcon icon={faCalendar} />{" "}
                                              <span>
                                                {moment(eventDetailsHover?.event?.start, "DD-MM-YYYY")?.format("ll")}{" "}
                                                {eventDetailsHover?.event?.end &&
                                                  eventDetailsHover?.event?.end !== eventDetailsHover?.event?.start ? (
                                                  <>
                                                    {" "}
                                                    <FontAwesomeIcon icon={faLongArrowRight} />{" "}
                                                    {moment(eventDetailsHover?.event?.end, "DD-MM-YYYY")?.format("ll")}
                                                  </>
                                                ) : null}
                                              </span>
                                            </div>
                                            {eventDetailsHover?.event?.total_participant_count != null ? (
                                              <div className="ui-events-calendar-hover-row">
                                                <FontAwesomeIcon icon={faUserGroup} />{" "}
                                                <span>
                                                  {eventDetailsHover?.event?.total_participant_count} {t("players")}
                                                </span>
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {!eventsByDate[dayString] && displayNewEvent === dayString ? (
                  <Button
                    type="primary"
                    size="small"
                    className="mt-auto"
                    onClick={e => {
                      e.stopPropagation();
                      resetPresetSettings();
                      setPresetSettings(prev => ({
                        ...prev,
                        start_date: moment(day).startOf("day").toDate(),
                        end_date: moment(day).startOf("day").toDate(),
                      }));
                      void openNewEventDrawer();
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" /> {t("New event")}
                  </Button>
                ) : null}
              </div>
            );
          } else {
            // Empty blank day for prev/next month padding
            return (
              <div key={index} className={"ui-events-calendar-day"}>
                {" "}
              </div>
            );
          }
        })}

        {loadingEvents ? (
          <div className="ui-events-calendar-overlay">
            <p className="ui-events-calendar-overlay-text">
              <FontAwesomeIcon icon={faSpinnerThird} spin /> {t("Loading events...")}
            </p>
          </div>
        ) : null}
      </div>

      {/* Legend */}
      <br />
      <div className="ui-events-calendar-legend">
        <p className="text-semibold">{t("Legend")}</p>
        <div className="flex items-center gap-2">
          <Badge outline size="medium" colour="#026AA2" backgroundColour="#FFFFFF">
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
            {eventCounts?.leagues} {eventCounts?.leagues === 1 ? t("League") : t("Leagues")}
          </Badge>
          <Badge outline size="medium" colour="#027A48" backgroundColour="#FFFFFF">
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
            {eventCounts?.tournaments} {eventCounts?.tournaments === 1 ? t("Tournament") : t("Tournaments")}
          </Badge>
        </div>
        <div className="ui-events-calendar-legend-divider" />
        <div className="flex items-center gap-2">
          <Badge outline size="medium" colour="#C11574" backgroundColour="#FFFFFF">
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
            {eventCounts?.weddings} {eventCounts?.weddings === 1 ? t("Wedding") : t("Weddings")}
          </Badge>
          <Badge outline size="medium" colour="#B93815" backgroundColour="#FFFFFF">
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
            {eventCounts?.banquets} {eventCounts?.banquets === 1 ? t("Banquet") : t("Banquets")}
          </Badge>
          <Badge outline size="medium" colour="#5925DC" backgroundColour="#FFFFFF">
            <FontAwesomeIcon icon={faCircle} style={{ fontSize: "6px" }} className="mr-2" />
            {eventCounts?.other} {t("Other")}
          </Badge>
        </div>
      </div>

      <NewEventDrawer
        open={newEventDrawerOpen}
        mode={eventDrawerMode}
        onClose={() => {
          void resetPresetSettings();
          void closeNewEventDrawer();
        }}
        onOk={settings => saveEvent(settings)}
        presetSettings={presetSettings}
        editActions={
          eventDrawerMode === "edit"
            ? {
              onViewMoreDetails: handleViewEventDetails,
              onDuplicate: () => setIsDuplicateEventWarningOpen(true),
              onDelete: () => setIsDeleteEventWarningOpen(true),
            }
            : undefined
        }
        handleOpenScheduler={(key: "template" | "tee-sheet", eventId, eventType, tournamentType) => updateScheduleModal({ isOpen: true, key, eventId, eventType, tournament_type: tournamentType })}
        applicationRefresh={applicationRefresh}
        onApplicationEditOpen={(application, key, eventId, eventType, tournamentType) => updateScheduleModal({ isOpen: true, key, eventId, eventType, application, tournament_type: tournamentType })}
      />

      <Portal isMounted={isDuplicateEventWarningOpen}>
        <Popup
          open={isDuplicateEventWarningOpen}
          type="warning"
          title={t("Duplicate Event")}
          description={t("Are you sure you want to duplicate this event?")}
          onCancel={() => setIsDuplicateEventWarningOpen(false)}
          onOk={duplicateEvent}
          okText={t("Duplicate")}
        />
      </Portal>

      <Portal isMounted={isDeleteEventWarningOpen}>
        <Popup
          open={isDeleteEventWarningOpen}
          type="warning"
          title={t("Delete Event")}
          description={t("Are you sure you want to delete this event?")}
          onCancel={() => setIsDeleteEventWarningOpen(false)}
          onOk={deleteEvent}
          okText={t("Delete")}
        />
      </Portal>

      <ScheduleEventModal
        open={scheduleModal?.isOpen}
        onClose={() => {
          void closeScheduleModal();
          setApplicationRefresh(0);
        }}
        startPage={scheduleModal?.key}
        onOk={(params, type, shotgun) => handleApplyEvent(params, type, shotgun)}
        application={scheduleModal?.application}
        tournamentType={scheduleModal?.tournament_type}
      />
    </>
  );
}
